import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Plus,
  Search,
  Filter,
  BarChart2,
  Clock,
  Calendar,
  Package,
  Building2,
  AlertTriangle,
  Printer,
  ScanLine,
  ChevronRight,
  ArrowUpRight,
  ArrowDownRight,
  LayoutGrid,
  List,
  X,
  Menu
} from 'lucide-react';

// Utility Components
const GlassCard = ({ children, className = '' }) => (
  <div className={`bg-white/60 backdrop-blur-sm rounded-2xl shadow-lg border border-white/20 ${className}`}>
    {children}
  </div>
);

const StatusBadge = ({ status }) => {
  const styles = {
    'Active': 'bg-green-100 text-green-800 border-green-200',
    'Issue Reported': 'bg-red-100 text-red-800 border-red-200',
    'Completed': 'bg-blue-100 text-blue-800 border-blue-200',
    'Pending QC': 'bg-yellow-100 text-yellow-800 border-yellow-200'
  };

  return (
    <span className={`px-2 py-1 text-xs font-medium rounded-full border ${styles[status] || 'bg-gray-100 text-gray-800 border-gray-200'}`}>
      {status}
    </span>
  );
};

const TrendIndicator = ({ value, className = '' }) => {
  const isPositive = value >= 0;
  return (
    <div className={`flex items-center gap-1 text-sm ${
      isPositive ? 'text-emerald-600' : 'text-rose-600'
    } ${className}`}>
      {isPositive ? (
        <ArrowUpRight className="w-4 h-4" />
      ) : (
        <ArrowDownRight className="w-4 h-4" />
      )}
      <span>{Math.abs(value)}%</span>
    </div>
  );
};

const BatchControlDashboard = () => {
  const navigate = useNavigate();
  const [isMobile, setIsMobile] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [batches, setBatches] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [locationFilter, setLocationFilter] = useState('All');
  const [dateRange, setDateRange] = useState('7d');
  const [viewMode, setViewMode] = useState('list');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    const generateMockBatches = () => {
      const locations = ['Central Kitchen 1', 'Central Kitchen 2', 'Production Unit A'];
      const products = ['Sauce Base', 'Marinade Mix', 'Spice Blend', 'Dough Mix', 'Dessert Base'];
      const shifts = ['Morning', 'Afternoon', 'Night'];
      const chefs = ['Ahmed Ali', 'Sara Khan', 'Mohammed Hassan', 'Fatima Abbas'];

      return Array.from({ length: 50 }, (_, i) => {
        const date = new Date();
        date.setDate(date.getDate() - Math.floor(Math.random() * 30));
        
        const location = locations[Math.floor(Math.random() * locations.length)];
        const shift = shifts[Math.floor(Math.random() * shifts.length)];
        const product = products[Math.floor(Math.random() * products.length)];
        const chef = chefs[Math.floor(Math.random() * chefs.length)];
        
        return {
          id: i + 1,
          batchNumber: `${location.substring(0, 2)}${shift[0]}${date.getFullYear().toString().slice(-2)}${String(i + 1).padStart(4, '0')}`,
          product,
          location,
          date: date.toISOString(),
          shift,
          quantity: Math.floor(Math.random() * 500) + 100,
          chef,
          team: Array.from({ length: Math.floor(Math.random() * 3) + 2 }, () => 
            chefs[Math.floor(Math.random() * chefs.length)]
          ),
          status: Math.random() > 0.9 ? 'Issue Reported' : 'Active',
          expiryDate: new Date(date.getTime() + (30 * 24 * 60 * 60 * 1000)).toISOString(),
          trend: (Math.random() * 20 - 10).toFixed(1)
        };
      });
    };

    setBatches(generateMockBatches());
    setLoading(false);
  }, []);

// Calculate stats
const calculateTrend = (current, previous) => {
  if (!previous || previous === 0) return 0;
  return ((current - previous) / previous * 100).toFixed(1);
};

const stats = {
  totalBatches: batches.length,
  activeBatches: {
    current: batches.filter(b => b.status === 'Active').length,
    trend: calculateTrend(
      batches.filter(b => b.status === 'Active').length,
      batches.filter(b => {
        const date = new Date(b.date);
        const lastMonth = new Date();
        lastMonth.setMonth(lastMonth.getMonth() - 1);
        return b.status === 'Active' && date < lastMonth;
      }).length
    )
  },
  issuesReported: {
    current: batches.filter(b => b.status === 'Issue Reported').length,
    trend: calculateTrend(
      batches.filter(b => b.status === 'Issue Reported').length,
      batches.filter(b => {
        const date = new Date(b.date);
        const lastMonth = new Date();
        lastMonth.setMonth(lastMonth.getMonth() - 1);
        return b.status === 'Issue Reported' && date < lastMonth;
      }).length
    )
  },
  batchesThisMonth: {
    current: batches.filter(b => 
      new Date(b.date) > new Date(new Date().setDate(1))
    ).length,
    trend: calculateTrend(
      batches.filter(b => 
        new Date(b.date) > new Date(new Date().setDate(1))
      ).length,
      batches.filter(b => {
        const date = new Date(b.date);
        const lastMonth = new Date();
        lastMonth.setMonth(lastMonth.getMonth() - 1);
        return date > new Date(lastMonth.setDate(1)) && date < new Date(new Date().setDate(1));
      }).length
    )
  }
};

const handleScanClick = () => {
  navigate('/batch-control/scanner');
};

const filteredBatches = batches.filter(batch => {
  const matchesSearch = (
    batch.batchNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
    batch.product.toLowerCase().includes(searchTerm.toLowerCase()) ||
    batch.chef.toLowerCase().includes(searchTerm.toLowerCase())
  );
  const matchesLocation = locationFilter === 'All' || batch.location === locationFilter;
  return matchesSearch && matchesLocation;
});

// Mobile components
const MobileHeader = () => (
  <div className="bg-white sticky top-0 z-50 border-b">
    <div className="px-4 py-3">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-bold">Batch Control</h1>
        <button
          onClick={() => setMobileMenuOpen(true)}
          className="p-2 text-gray-500 hover:text-gray-700"
        >
          <Menu className="w-6 h-6" />
        </button>
      </div>
    </div>
  </div>
);

const MobileMenu = () => (
  <div className={`fixed inset-0 bg-black bg-opacity-50 z-50 transition-opacity duration-200 ${
    mobileMenuOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
  }`}>
    <div className={`fixed inset-y-0 right-0 w-64 bg-white shadow-xl transform transition-transform duration-200 ${
      mobileMenuOpen ? 'translate-x-0' : 'translate-x-full'
    }`}>
      <div className="p-4">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-lg font-semibold">Menu</h2>
          <button
            onClick={() => setMobileMenuOpen(false)}
            className="p-2 text-gray-500 hover:text-gray-700"
          >
            <X className="w-6 h-6" />
          </button>
        </div>
        <nav className="space-y-4">
          <Link 
            to="/batch-control/scanner"
            className="flex items-center space-x-2 p-2 hover:bg-gray-100 rounded-lg"
          >
            <ScanLine className="w-5 h-5" />
            <span>Scan Batch</span>
          </Link>
          <Link 
            to="/batch-control/new"
            className="flex items-center space-x-2 p-2 hover:bg-gray-100 rounded-lg"
          >
            <Plus className="w-5 h-5" />
            <span>Create Batch</span>
          </Link>
        </nav>
      </div>
    </div>
  </div>
);

const MobileFilters = () => (
  <div className={`fixed inset-0 bg-black bg-opacity-50 z-50 transition-opacity duration-200 ${
    mobileFiltersOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
  }`}>
    <div className={`fixed inset-y-0 left-0 w-64 bg-white shadow-xl transform transition-transform duration-200 ${
      mobileFiltersOpen ? 'translate-x-0' : '-translate-x-full'
    }`}>
      <div className="p-4">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-lg font-semibold">Filters</h2>
          <button
            onClick={() => setMobileFiltersOpen(false)}
            className="p-2 text-gray-500 hover:text-gray-700"
          >
            <X className="w-6 h-6" />
          </button>
        </div>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Location
            </label>
            <select
              value={locationFilter}
              onChange={(e) => setLocationFilter(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg"
            >
              <option value="All">All Locations</option>
              {[...new Set(batches.map(b => b.location))].map(location => (
                <option key={location} value={location}>{location}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Date Range
            </label>
            <select
              value={dateRange}
              onChange={(e) => setDateRange(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg"
            >
              <option value="7d">Last 7 Days</option>
              <option value="30d">Last 30 Days</option>
              <option value="90d">Last 90 Days</option>
            </select>
          </div>
        </div>
      </div>
    </div>
  </div>
);

const MobileBatchCard = ({ batch }) => (
  <div className="bg-white rounded-lg border p-4 space-y-3">
    <div className="flex justify-between items-start">
      <div>
        <div className="font-medium">{batch.batchNumber}</div>
        <div className="text-sm text-gray-500">{batch.product}</div>
      </div>
      <StatusBadge status={batch.status} />
    </div>
    <div className="grid grid-cols-2 gap-2 text-sm">
      <div>
        <span className="text-gray-500">Location</span>
        <p className="font-medium">{batch.location}</p>
      </div>
      <div>
        <span className="text-gray-500">Quantity</span>
        <p className="font-medium">{batch.quantity} units</p>
      </div>
    </div>
    <div className="pt-3 border-t flex justify-between items-center">
      <button
        className="text-sm text-gray-600 flex items-center gap-1"
        onClick={() => {/* Handle print */}}
      >
        <Printer className="w-4 h-4" />
        Print
      </button>
      <Link
        to={`/batch-control/${batch.id}`}
        className="text-sm text-indigo-600 flex items-center gap-1"
      >
        View Details
        <ChevronRight className="w-4 h-4" />
      </Link>
    </div>
  </div>
);

if (loading) {
  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
    </div>
  );
}

if (isMobile) {
  return (
    <div className="min-h-screen bg-gray-50">
      <MobileHeader />
      <MobileMenu />
      <MobileFilters />
      
      {/* Stats Cards */}
      <div className="p-4 grid grid-cols-2 gap-4">
        <GlassCard className="p-4">
          <div className="flex items-center gap-2">
            <Package className="w-8 h-8 text-emerald-500 bg-emerald-100 p-1.5 rounded-lg" />
            <div>
              <p className="text-sm text-gray-500">Active</p>
              <p className="text-lg font-bold">{stats.activeBatches.current}</p>
            </div>
          </div>
        </GlassCard>
        <GlassCard className="p-4">
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-8 h-8 text-rose-500 bg-rose-100 p-1.5 rounded-lg" />
            <div>
              <p className="text-sm text-gray-500">Issues</p>
              <p className="text-lg font-bold">{stats.issuesReported.current}</p>
            </div>
          </div>
        </GlassCard>
      </div>

      {/* Search Bar */}
      <div className="sticky top-14 bg-white z-40 px-4 py-2 border-b">
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
            <input
              type="text"
              placeholder="Search batches..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-gray-50 border border-gray-200 rounded-lg"
            />
          </div>
          <button
            onClick={() => setMobileFiltersOpen(true)}
            className="p-2 bg-gray-50 border border-gray-200 rounded-lg"
          >
            <Filter size={20} className="text-gray-500" />
          </button>
        </div>
      </div>

      {/* Batch List */}
      <div className="p-4 pb-20">
        <div className="space-y-4">
          {filteredBatches.map(batch => (
            <MobileBatchCard key={batch.id} batch={batch} />
          ))}
        </div>
      </div>

      {/* Floating Action Buttons */}
      <div className="fixed bottom-4 right-4 flex flex-col gap-2">
        <Link
          to="/batch-control/scanner"
          className="p-3 bg-indigo-100 text-indigo-600 rounded-full shadow-lg"
        >
          <ScanLine className="w-6 h-6" />
        </Link>
        <Link
          to="/batch-control/new"
          className="p-3 bg-indigo-600 text-white rounded-full shadow-lg"
        >
          <Plus className="w-6 h-6" />
        </Link>
      </div>
    </div>
  );
}

// Desktop version remains unchanged
// Replace the final return statement with this:
return (
  <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-6">
    {/* Header Section */}
    <div className="mb-8">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-4xl font-bold text-gray-800 mb-2">Batch Control</h1>
          <p className="text-gray-500">Managing production batches and traceability</p>
        </div>
        <div className="flex gap-4">
          <button
            onClick={handleScanClick}
            className="inline-flex items-center px-6 py-3 bg-indigo-100 text-indigo-700 rounded-xl transition-all duration-200 hover:bg-indigo-200"
          >
            <ScanLine className="w-5 h-5 mr-2" />
            Scan Batch
          </button>
          <Link 
            to="/batch-control/new" 
            className="inline-flex items-center px-6 py-3 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl transition-all duration-200 shadow-lg hover:shadow-xl"
          >
            <Plus className="w-5 h-5 mr-2" />
            Create Batch
          </Link>
        </div>
      </div>
    </div>

    {/* Stats Overview */}
    <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
      <GlassCard className="p-6 hover:shadow-xl transition-shadow duration-200">
        <div className="flex items-center">
          <Package className="w-12 h-12 text-emerald-500 bg-emerald-100 p-2 rounded-xl" />
          <div className="ml-4">
            <p className="text-sm text-gray-500">Total Batches</p>
            <h3 className="text-2xl font-bold text-gray-800">{stats.totalBatches}</h3>
          </div>
        </div>
      </GlassCard>
      
      <GlassCard className="p-6 hover:shadow-xl transition-shadow duration-200">
        <div className="flex items-center">
          <Calendar className="w-12 h-12 text-blue-500 bg-blue-100 p-2 rounded-xl" />
          <div className="ml-4">
            <p className="text-sm text-gray-500">This Month</p>
            <div className="flex items-center gap-2">
              <h3 className="text-2xl font-bold text-gray-800">
                {stats.batchesThisMonth.current}
              </h3>
              <TrendIndicator value={stats.batchesThisMonth.trend} />
            </div>
          </div>
        </div>
      </GlassCard>

      <GlassCard className="p-6 hover:shadow-xl transition-shadow duration-200">
        <div className="flex items-center">
          <Clock className="w-12 h-12 text-amber-500 bg-amber-100 p-2 rounded-xl" />
          <div className="ml-4">
            <p className="text-sm text-gray-500">Active Batches</p>
            <div className="flex items-center gap-2">
              <h3 className="text-2xl font-bold text-gray-800">
                {stats.activeBatches.current}
              </h3>
              <TrendIndicator value={stats.activeBatches.trend} />
            </div>
          </div>
        </div>
      </GlassCard>

      <GlassCard className="p-6 hover:shadow-xl transition-shadow duration-200">
        <div className="flex items-center">
          <AlertTriangle className="w-12 h-12 text-rose-500 bg-rose-100 p-2 rounded-xl" />
          <div className="ml-4">
            <p className="text-sm text-gray-500">Issues Reported</p>
            <div className="flex items-center gap-2">
              <h3 className="text-2xl font-bold text-gray-800">
                {stats.issuesReported.current}
              </h3>
              <TrendIndicator value={stats.issuesReported.trend} />
            </div>
          </div>
        </div>
      </GlassCard>
    </div>

    {/* Search and Filters */}
    <GlassCard className="p-4 mb-8">
      <div className="flex flex-wrap gap-4 items-center">
        <div className="flex-1 min-w-[200px]">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
            <input
              type="text"
              placeholder="Search by batch number, product, or chef..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-3 bg-white/50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>
        </div>
        <div className="flex gap-4 items-center">
          <Filter size={20} className="text-gray-400" />
          <select
            value={locationFilter}
            onChange={(e) => setLocationFilter(e.target.value)}
            className="bg-white/50 border border-gray-200 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-indigo-500"
          >
            <option value="All">All Locations</option>
            {[...new Set(batches.map(b => b.location))].map(location => (
              <option key={location} value={location}>{location}</option>
            ))}
          </select>
          <select
            value={dateRange}
            onChange={(e) => setDateRange(e.target.value)}
            className="bg-white/50 border border-gray-200 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-indigo-500"
          >
            <option value="7d">Last 7 Days</option>
            <option value="30d">Last 30 Days</option>
            <option value="90d">Last 90 Days</option>
            <option value="all">All Time</option>
          </select>
        </div>
      </div>
    </GlassCard>

    {/* Batch List */}
    <GlassCard className="overflow-hidden">
      <div className="p-6 flex justify-between items-center">
        <h2 className="text-xl font-semibold">Batches ({filteredBatches.length})</h2>
        <div className="flex gap-2">
          <button
            onClick={() => setViewMode('grid')}
            className={`p-2 rounded-lg ${viewMode === 'grid' 
              ? 'bg-indigo-100 text-indigo-600' 
              : 'text-gray-500 hover:bg-gray-100'}`}
          >
            <LayoutGrid size={20} />
          </button>
          <button
            onClick={() => setViewMode('list')}
            className={`p-2 rounded-lg ${viewMode === 'list' 
              ? 'bg-indigo-100 text-indigo-600' 
              : 'text-gray-500 hover:bg-gray-100'}`}
          >
            <List size={20} />
          </button>
        </div>
      </div>

      {viewMode === 'list' ? (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50/50">
                <th className="px-6 py-4 text-left text-sm font-medium text-gray-500">Batch Number</th>
                <th className="px-6 py-4 text-left text-sm font-medium text-gray-500">Product</th>
                <th className="px-6 py-4 text-left text-sm font-medium text-gray-500">Location</th>
                <th className="px-6 py-4 text-left text-sm font-medium text-gray-500">Date</th>
                <th className="px-6 py-4 text-left text-sm font-medium text-gray-500">Chef</th>
                <th className="px-6 py-4 text-left text-sm font-medium text-gray-500">Status</th>
                <th className="px-6 py-4 text-left text-sm font-medium text-gray-500">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200/50">
              {filteredBatches.map((batch) => (
                <tr key={batch.id} className="hover:bg-gray-50/30">
                  <td className="px-6 py-4">
                    <div className="font-medium text-gray-900">{batch.batchNumber}</div>
                    <div className="text-sm text-gray-500">{batch.shift} Shift</div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm font-medium text-gray-900">{batch.product}</div>
                    <div className="text-sm text-gray-500">{batch.quantity} units</div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-gray-900">{batch.location}</div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-gray-900">
                      {new Date(batch.date).toLocaleDateString()}
                    </div>
                    <div className="text-xs text-gray-500">
                      Expires: {new Date(batch.expiryDate).toLocaleDateString()}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-gray-900">{batch.chef}</div>
                    <div className="text-xs text-gray-500">
                      {batch.team.length} team members
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <StatusBadge status={batch.status} />
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex space-x-3">
                      <button
                        className="text-indigo-600 hover:text-indigo-900 text-sm font-medium flex items-center"
                      >
                        <Printer className="w-4 h-4 mr-1" />
                        Print
                      </button>
                      <Link
                        to={`/batch-control/${batch.id}`}
                        className="text-indigo-600 hover:text-indigo-900 text-sm font-medium flex items-center"
                      >
                        <ChevronRight className="w-4 h-4 mr-1" />
                        Details
                      </Link>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 p-6">
          {filteredBatches.map((batch) => (
            <div 
              key={batch.id}
              className="bg-white rounded-xl border border-gray-200 overflow-hidden hover:shadow-lg transition-shadow duration-200"
            >
              <div className="p-6">
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <div className="font-medium text-gray-900">{batch.batchNumber}</div>
                    <div className="text-sm text-gray-500">{batch.product}</div>
                  </div>
                  <StatusBadge status={batch.status} />
                </div>

                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-gray-500">Location</span>
                      <p className="font-medium text-gray-900">{batch.location}</p>
                    </div>
                    <div>
                      <span className="text-gray-500">Chef</span>
                      <p className="font-medium text-gray-900">{batch.chef}</p>
                    </div>
                    <div>
                      <span className="text-gray-500">Production Date</span>
                      <p className="font-medium text-gray-900">
                        {new Date(batch.date).toLocaleDateString()}
                      </p>
                    </div>
                    <div>
                      <span className="text-gray-500">Quantity</span>
                      <p className="font-medium text-gray-900">{batch.quantity} units</p>
                    </div>
                  </div>

                  <div className="pt-4 flex justify-between items-center border-t">
                    <button
                      className="text-indigo-600 hover:text-indigo-900 text-sm font-medium flex items-center"
                    >
                      <Printer className="w-4 h-4 mr-1" />
                      Print Label
                    </button>
                    <Link
                      to={`/batch-control/${batch.id}`}
                      className="text-indigo-600 hover:text-indigo-900 text-sm font-medium flex items-center"
                    >
                      View Details
                      <ChevronRight className="w-4 h-4 ml-1" />
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </GlassCard>
  </div>
);
};

export default BatchControlDashboard;